import { TriangleAlert } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from "@/components/ui/empty";

interface ErrorStateProps {
  /** Headline, e.g. "Couldn't load devices". */
  title?: string;
  description?: string;
  /** Wire to the failing query's `refetch`. Renders a retry button when set. */
  onRetry?: () => void;
  isRetrying?: boolean;
  className?: string;
}

/**
 * Standard "couldn't load this" state for a data page whose query failed.
 * Distinct from the empty state (which means "no data yet"): a query failure
 * should tell the user something went wrong and let them retry, not silently
 * render an empty list.
 */
export function ErrorState({
  title = "Couldn't load this",
  description = "Something went wrong while loading. Check your connection and try again.",
  onRetry,
  isRetrying = false,
  className,
}: ErrorStateProps) {
  return (
    <Empty className={className} data-testid="error-state">
      <EmptyHeader>
        <EmptyMedia variant="icon">
          <TriangleAlert className="text-destructive" />
        </EmptyMedia>
        <EmptyTitle>{title}</EmptyTitle>
        <EmptyDescription>{description}</EmptyDescription>
      </EmptyHeader>
      {onRetry && (
        <EmptyContent>
          <Button variant="outline" onClick={onRetry} disabled={isRetrying}>
            {isRetrying ? "Retrying…" : "Try again"}
          </Button>
        </EmptyContent>
      )}
    </Empty>
  );
}
